import { c as _c } from "react/compiler-runtime";
import { ActionMenu, ActionMenuItem, FormInput } from '@systemeio/ui';
import { CreateModuleErrorsInterface, CreateModuleInterface, ModuleId } from 'modules/courses/courses/curriculum/types/module-interface';
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props';
import { CourseId } from 'modules/courses/courses/types/course-interface';
import { memo, useState } from 'react';
import Confirmation from 'shared/components/confirmation-modal';
import EditModal from 'shared/components/edit-modal';
import { RequestModuleEnum } from 'shared/enums/request-module-enum';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules';
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors';
import { KeyedMutator } from 'swr';
import { useModuleApi } from '../../curriculum/hooks/use-module-api';
import { useLightCourseActions } from '../hooks/use-light-course-actions';
import { LightCourseCurriculumInterface } from '../light-course-interface';
export type LightCourseModuleActionsProps = {
  module: LightCourseCurriculumInterface['modules'][number];
  mutate: KeyedMutator<LightCourseCurriculumInterface>;
};
const defaultErrors: CreateModuleErrorsInterface = {
  fields: {
    name: ''
  }
};
export const LightCourseModuleActions = memo(function LightCourseModuleActions(t0) {
  const $ = _c(52);
  const {
    module,
    mutate
  } = t0;
  const {
    t
  } = useLocoTranslation();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [editErrors, setEditErrors] = useState(defaultErrors);
  const caption = t("dashboard.course.module_title");
  const {
    removeModuleWithLectures
  } = useModuleApi();
  const {
    updateLightCourseModule
  } = useLightCourseActions();
  const {
    revalidateModules
  } = useRevalidateModules();
  const hasLectures = module.lectures?.length > 0;
  let t1;
  if ($[0] !== mutate || $[1] !== removeModuleWithLectures || $[2] !== revalidateModules) {
    t1 = async moduleId => {
      await removeModuleWithLectures(moduleId);
      await mutate(data => {
        if (data) {
          return {
            ...data,
            modules: data.modules.filter(module_0 => module_0.id !== moduleId)
          };
        }
      }, false);
      await revalidateModules([RequestModuleEnum.MembershipLightCourseCurriculum, RequestModuleEnum.MembershipCourseLecture]);
    };
    $[0] = mutate;
    $[1] = removeModuleWithLectures;
    $[2] = revalidateModules;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const onRemoveModule = t1;
  let t2;
  if ($[4] !== mutate || $[5] !== revalidateModules || $[6] !== updateLightCourseModule) {
    t2 = async (moduleId_0, data_0) => {
      await fetchWithFieldErrors(async () => {
        const response = await updateLightCourseModule(moduleId_0, data_0);
        await mutate(data_1 => {
          if (data_1) {
            return {
              ...data_1,
              modules: data_1.modules.map(module_1 => {
                if (module_1.id === moduleId_0) {
                  return response;
                }
                return module_1;
              })
            };
          }
        }, false);
      }, setEditErrors);
      await revalidateModules([RequestModuleEnum.MembershipLightCourseCurriculum]);
    };
    $[4] = mutate;
    $[5] = revalidateModules;
    $[6] = updateLightCourseModule;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onEditModule = t2;
  const t3 = "flex items-center justify-end";
  const t4 = nonDraggableProps;
  let t5;
  if ($[8] !== t) {
    t5 = t("dashboard.actions.title.settings");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setIsSettingsOpen(true);
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5) {
    t7 = <ActionMenuItem key="module-settings" label={t5} onClick={t6} />;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t) {
    t8 = t("dashboard.actions.title.remove");
    $[13] = t;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsRemoveOpen(true);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8) {
    t10 = <ActionMenuItem key="module-remove" label={t8} onClick={t9} />;
    $[16] = t8;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t7) {
    t11 = <ActionMenu menuItems={[t7, t10]} />;
    $[18] = t10;
    $[19] = t7;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t) {
    t12 = t("dashboard.course.module_edit_settings_title");
    $[21] = t;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setIsSettingsOpen(false);
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== editErrors || $[25] !== t) {
    t14 = [{
      field: "name",
      onRender: (value, onChange) => <FormInput key="module-edit-name" value={value} onChange={e => onChange(e.target.value)} label={t("global.name")} error={editErrors.fields.name} />
    }];
    $[24] = editErrors;
    $[25] = t;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== module.id || $[28] !== onEditModule) {
    t15 = data_2 => onEditModule(module.id, data_2);
    $[27] = module.id;
    $[28] = onEditModule;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== isSettingsOpen || $[31] !== module || $[32] !== t12 || $[33] !== t14 || $[34] !== t15) {
    t16 = <EditModal fullCaption={t12} opened={isSettingsOpen} onClose={t13} instance={module} editable={t14} onEdit={t15} />;
    $[30] = isSettingsOpen;
    $[31] = module;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const T0 = Confirmation;
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => setIsRemoveOpen(false);
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== module.id || $[38] !== onRemoveModule) {
    t18 = () => onRemoveModule(module.id);
    $[37] = module.id;
    $[38] = onRemoveModule;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const t19 = t(hasLectures ? "dashboard.actions.remove_with_lectures_confirmation" : "dashboard.actions.remove_confirmation", {
    module: caption?.toLowerCase()
  });
  const t20 = t("dashboard.actions.removed", {
    module: caption?.toLowerCase()
  });
  let t21;
  if ($[40] !== T0 || $[41] !== isRemoveOpen || $[42] !== t17 || $[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <T0 opened={isRemoveOpen} onClose={t17} onConfirm={t18} confirmationText={t19} toastCaption={t20} />;
    $[40] = T0;
    $[41] = isRemoveOpen;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t11 || $[48] !== t16 || $[49] !== t21 || $[50] !== t4) {
    t22 = <div className={t3}><div {...t4}>{t11}{t16}{t21}</div></div>;
    $[47] = t11;
    $[48] = t16;
    $[49] = t21;
    $[50] = t4;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  return t22;
});